import { DatePicker, Parameters } from '@/components/controls';
import { useAggregate } from '@/hooks';
import { downloadCSV } from '@/utils';
import { Download as DownloadIcon } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import {
  MaterialReactTable,
  MRT_TablePagination,
  useMaterialReactTable,
} from 'material-react-table';
import { useMemo } from 'react';
import { columns, pipelineFn, stateAtom } from './utils';

export function Mileage() {
  const [{ query, parameters, time }, setState] = useAtom(stateAtom);
  const collection = 'vehicles';
  const pipeline = useMemo(() => pipelineFn(query, time), [query, time]);
  const {
    query: { data, isFetching },
    cancel,
  } = useAggregate(collection, pipeline, true, 1000 * 60 * 60);

  function handleDownloadClick() {
    const csvColumns = columns.flatMap((column) => {
      if (column.columns) {
        return column.columns.map((child) => ({
          label: `${column.header} ${child.header}`,
          key: child.id ?? child.accessorKey,
        }));
      }

      return [
        {
          label: column.header,
          key: column.id ?? column.accessorKey,
        },
      ];
    });

    downloadCSV(data, 'mileage.csv', csvColumns);
  }

  function renderBottomToolbar() {
    return (
      <Stack
        direction="row"
        sx={{ pl: 1, justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box>
          <Tooltip title="Download data">
            <IconButton size="small" onClick={handleDownloadClick}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <MRT_TablePagination table={table} />
      </Stack>
    );
  }

  function handleCancel() {
    cancel();
    setState((state) => ({ ...state, query: {} }));
  }

  function handleTimeChange(value) {
    setState((state) => ({ ...state, time: value }));
  }

  const handleStateChange = (name) => (value) => {
    setState((state) => ({ ...state, [name]: value }));
  };

  const handleStateChangeWithEvent = (name) => (_, value) => {
    setState((state) => ({ ...state, [name]: value }));
  };

  const table = useMaterialReactTable({
    columns,
    data,
    initialState: {
      sorting: [{ id: 'registrationNumber' }],
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
    state: { density: 'compact', isLoading: isFetching },
    defaultColumn: { size: 0, enableEditing: false },
    renderBottomToolbar,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableTopToolbar: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    muiTablePaperProps: { elevation: 0, variant: 'elevation' },
    muiTableContainerProps: {
      sx: {
        width: 'calc(100vw - 280px)',
        maxHeight: 'calc(100vh - 160px)', // 48 app header + 56 top bar + 56 bottom bar
      },
    },
    muiTableHeadRowProps: ({ headerGroup }) => ({
      sx: {
        position: 'sticky',
        top: `${headerGroup.depth * 32}px`,
      },
    }),
  });

  return (
    <Stack direction="row">
      <title>IR3 | Mileage</title>
      <Box
        sx={{
          width: 280,
          height: 'calc(100vh - 48px)',
          borderRight: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Parameters
          dateOnly
          hideTimePeriod
          collection={collection}
          onFetch={handleStateChangeWithEvent('query')}
          onCancel={handleCancel}
          isFetching={isFetching}
          value={parameters}
          onChange={handleStateChange('parameters')}
          sx={{ pt: 1, height: 1 }}
          vehicle
        >
          <Typography variant="subtitle2" color="textSecondary">
            Date
          </Typography>
          <DatePicker
            value={time}
            onChange={handleTimeChange}
            clearable
            disableFuture
          />
        </Parameters>
      </Box>
      <MaterialReactTable table={table} />
    </Stack>
  );
}
